import { Receipt } from 'lucide-react';
import type { PromotionMetricsTransaction } from '@/client/types/promotion';
import { money, type ColorScale } from './analytics';

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

export const RecentTransactionsTable: React.FC<{
  orders: PromotionMetricsTransaction[];
  categoryColor: ColorScale;
  tierColor: ColorScale;
}> = ({ orders, categoryColor, tierColor }) => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
    <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
      <Receipt className="h-4 w-4 text-emerald-600" />
      <h3 className="text-sm font-semibold text-gray-900">Recent purchases</h3>
    </div>
    {orders.length === 0 ? (
      <p className="py-8 text-center text-sm text-gray-400">No purchases in this period.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {orders.map((o) => (
              <tr key={o.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-5 py-2.5 text-gray-500">{fmtDate(o.date)}</td>
                <td className="px-5 py-2.5">
                  <span className="truncate font-medium text-gray-900">{o.buyer}</span>
                  {o.eid != null && (
                    <span className="ml-1.5 font-mono text-[11px] text-gray-400">#{o.eid}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-5 py-2.5">
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                    style={{ backgroundColor: `${categoryColor(o.category)}1a`, color: categoryColor(o.category) }}
                  >
                    {o.category}
                  </span>
                </td>
                <td className="whitespace-nowrap px-5 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: tierColor(o.tier) }} />
                    {o.tier}
                  </span>
                </td>
                <td className="whitespace-nowrap px-5 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                  {money(o.amount)}
                </td>
                <td className="whitespace-nowrap px-5 py-2.5 text-right">
                  {o.status === 'refunded' ? (
                    <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-600">Refunded</span>
                  ) : (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Paid</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
